import { Component, EventEmitter, Output, HostListener, ElementRef, OnInit, input, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FilterImportResults, FilterSectionsToUpdate, ImportFilterConfirm } from '../models/filter-import.models';
import { ChanceBaseTierType, Tier, UniqueTierType } from '../models/item-data-models';
import { RulePreviewComponent } from '../common/rule-preview/rule-preview.component';
import { DisplayOptions, Filter, PreviewCustomRule } from '../models/filter-models';
import { flattenAllRulesInGroupsByGroupOrder, totalRulesInCustomGroups } from '../common/custom-rules/custom-rule-groups-helper';
import { Rarity } from "../models/enums";
import { CurrencyTierService } from '../services/tiering/currency-tier.service';
import { UniquesTierService } from '../services/tiering/uniques-tier.service';
import { ChanceBasesTierService } from '../services/tiering/chance-bases-tier.service';
import { FilterService } from '../services/filter.service';
import { CosmeticPresetsService } from '../services/cosmetic-presets.service';
import { LOCAL_STORAGE_KEY_ACCOUNT_FILTERS, MAX_FILTER_NAME_LENGTH } from '../const';
import { PoeFilter } from '../services/poe-api.service';
import { BaseComponent } from '../common/base.component';

@Component({
  selector: 'app-import-filter-dialog',
  imports: [RulePreviewComponent, FormsModule],
  templateUrl: './import-filter-dialog.component.html',
  styleUrls: ['../common/dialogs.scss', './import-filter-dialog.component.scss']
})
export class ImportFilterDialogComponent extends BaseComponent implements OnInit {
  filterResults = input.required<FilterImportResults>();
  sectionsToUpdateInput = input(new FilterSectionsToUpdate(), { alias: 'sectionsToUpdate' });
  clone = input(false);
  apply = input(false);
  refreshFromOnline = input(false);
  refreshFromUsername = input<string | undefined>(undefined);
  title = input('Import filter');
  startAsNewFilter = input(false);

  @Output() close = new EventEmitter<void>();
  @Output() updateFilter = new EventEmitter<ImportFilterConfirm>();

  Tier = Tier;
  readonly maxFilterNameLength = MAX_FILTER_NAME_LENGTH;

  tierCosmeticPreviewRules: PreviewCustomRule[] = [];
  importedFilterPreviewRules: PreviewCustomRule[] = [];
  sectionsToUpdate = new FilterSectionsToUpdate();

  allCustomTiers = false;
  myEquipmentAndFlasks = false;

  importToNewFilter = signal(false);
  newFilterName = '';
  nameError: string | null = null;
  showRuleFineTune = signal(false);

  private readonly sectionsRevision = signal(0);

  isFileImport = computed(() => !this.clone() && !this.apply());
  refreshFromOnlineTitle = computed(() => {
    const name = this.filterResults().importedName;
    const username = this.refreshFromUsername();
    if (name && username) {
      return `Refresh from online: ${name} from ${username}`;
    }
    if (name) {
      return `Refresh from online: ${name}`;
    }
    return 'Refresh from online';
  });
  importCosmetics = computed(() => {
    this.sectionsRevision();
    return this.sectionsToUpdate.cosmeticsSelected;
  });
  importRules = computed(() => {
    this.sectionsRevision();
    return this.sectionsToUpdate.anyRuleSelected;
  });
  showRuleSections = computed(() => this.clone() || this.apply() || this.showRuleFineTune());
  showAppendOptions = computed(() => (this.apply() || !this.clone()) && !this.importToNewFilter());
  showCloneApplyTierCosmetics = computed(() => (this.clone() || this.apply()) && !!this.filterResults().filter?.tierCosmetics);
  showCloneApplyCustomCosmeticRules = computed(() => (this.clone() || this.apply()) && this.cosmeticImportRuleCount() > 0);
  actionVerb = computed(() => this.apply() ? 'Apply' : this.clone() ? 'Clone' : 'Import');
  primaryActionLabel = computed(() => {
    this.sectionsRevision();
    if (this.sectionsToUpdate.allSelected) {
      return `${this.actionVerb()} Filter`;
    }
    return `${this.actionVerb()} Selected Sections`;
  });
  cosmeticImportRuleCount = computed(() => totalRulesInCustomGroups(this.filterResults().filter?.customCosmeticRuleGroups));
  cosmeticImportRulesPreviewRules = computed(() => {
    const filter = this.filterResults().filter;
    return filter ? flattenAllRulesInGroupsByGroupOrder(filter.customCosmeticRuleGroups) : [];
  });
  activeFilterPreviewRules = computed(() =>
    this.buildCombinedCosmeticPreviewRules(this.filterService.activeFilter().filter)
  );
  defaultFilterPreviewRules = computed(() => {
    const defaultFilter = new Filter();
    this.cosmeticPresetsService.applyPreset(defaultFilter);
    return this.buildCombinedCosmeticPreviewRules(defaultFilter);
  });
  dropPreviewRules = computed(() => {
    if (this.importCosmetics()) return this.importedFilterPreviewRules;
    if (this.importToNewFilter()) return this.defaultFilterPreviewRules();
    return this.activeFilterPreviewRules();
  });

  constructor(
    private elementRef: ElementRef,
    private currencyTierService: CurrencyTierService,
    private uniquesTierService: UniquesTierService,
    private chanceBasesTierService: ChanceBasesTierService,
    private filterService: FilterService,
    private cosmeticPresetsService: CosmeticPresetsService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.sectionsToUpdate = this.sectionsToUpdateInput();
    const filter = this.filterResults().filter;
    if (!filter) return;

    if (this.startAsNewFilter()) {
      this.importToNewFilter.set(true);
      this.sectionsToUpdate = FilterSectionsToUpdate.allRulesOn();
    }
    this.newFilterName = this.getDefaultNewFilterName();
    this.tierCosmeticPreviewRules = this.buildTierCosmeticPreviewRules(filter);
    this.importedFilterPreviewRules = this.buildCombinedCosmeticPreviewRules(filter);

    this.allCustomTiers = this.sectionsToUpdate.customCurrencyAndDivCardTiers;
    this.syncGroupTogglesFromSections();
  }

  private buildTierCosmeticPreviewRules(filter: Filter): PreviewCustomRule[] {
    const rules: PreviewCustomRule[] = [];

    rules.push(...filter.tierCosmetics.map(c => new PreviewCustomRule({
      exampleName: this.currencyTierService.getFirstCurrencyForDropTier(c.tier!),
      displayOptions: c.displayOptions,
      itemClass: 'Stackable Currency',
    })) || []);

    rules.push(this.createCosmetics(this.getFirstUniqueForUniqueTier('Excellent'), false, Rarity.Unique, filter.uniqueTierCosmetics[0]!.displayOptions));
    rules.push(this.createCosmetics(this.getFirstUniqueForUniqueTier('Good'), false, Rarity.Unique, filter.uniqueTierCosmetics[1]!.displayOptions));
    rules.push(this.createCosmetics(this.getFirstUniqueForUniqueTier('Potential'), false, Rarity.Unique, filter.uniqueTierCosmetics[2]!.displayOptions));
    rules.push(this.createCosmetics(this.getFirstUniqueForUniqueTier('Other'), false, Rarity.Unique, filter.uniqueTierCosmetics[3]!.displayOptions));

    rules.push(this.createCosmetics('Stellar Amulet', false, Rarity.Normal, filter.chanceBaseTierCosmetics[0]!.displayOptions));
    rules.push(this.createCosmetics(this.getFirstChanceBaseForChanceBaseTier('Potential'), false, Rarity.Normal, filter.chanceBaseTierCosmetics[1].displayOptions));

    rules.forEach((rule: PreviewCustomRule, index: number) => {
      rule.order = index;
    });

    return rules;
  }

  private buildCombinedCosmeticPreviewRules(filter: Filter): PreviewCustomRule[] {
    const rules = [
      ...this.buildTierCosmeticPreviewRules(filter),
      ...flattenAllRulesInGroupsByGroupOrder(filter.customCosmeticRuleGroups).map(rule =>
        new PreviewCustomRule({ ...rule })
      ),
    ];

    rules.forEach((rule: PreviewCustomRule, index: number) => {
      rule.order = index;
    });

    return rules;
  }

  private createCosmetics(exampleName: string, isCurrency: boolean, rarity: Rarity, displayOptions: DisplayOptions) {
    const rule = new PreviewCustomRule({
      displayOptions,
      itemClass: isCurrency ? 'Stackable Currency' : undefined,
      exampleName,
      rarity,
    });

    return rule;
  }

  getFirstUniqueForUniqueTier(tier: UniqueTierType) {
    return this.uniquesTierService.getTieredUniques()[tier][0]?.baseType ?? "Unique";
  }

  getFirstChanceBaseForChanceBaseTier(tier: ChanceBaseTierType) {
    return this.chanceBasesTierService.getTieredChanceBases()[tier][0]?.baseType ?? "Chance Base";
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const dialogElement = this.elementRef.nativeElement.querySelector('.dialog');
    if (dialogElement && !dialogElement.contains(event.target)) {
      this.closeDialog();
    }
  }

  closeDialog() {
    this.close.emit();
  }

  importSections() {
    if (this.isFileImport() && this.importToNewFilter()) {
      this.nameError = null;
      if (!this.newFilterName.trim()) {
        this.nameError = 'Please enter a filter name.';
        return;
      }
      if (this.isFilterNameTaken(this.newFilterName)) {
        this.nameError = `"${this.newFilterName}" already exists. Please choose a different name.`;
        return;
      }
    }

    const confirm = new ImportFilterConfirm();
    confirm.sections = this.sectionsToUpdate;
    confirm.importToNewFilter = this.isFileImport() && this.importToNewFilter();
    confirm.newFilterName = this.newFilterName.trim();
    this.updateFilter.emit(confirm);
  }

  toggleImportToNewFilter() {
    this.importToNewFilter.update(v => !v);
    this.nameError = null;
    if (this.importToNewFilter()) {
      this.sectionsToUpdate = FilterSectionsToUpdate.allRulesOn();
      this.syncGroupTogglesFromSections();
    } else {
      this.sectionsToUpdate = FilterSectionsToUpdate.fileImportDefaults();
      this.syncGroupTogglesFromSections();
    }
  }

  toggleImportCosmetics() {
    this.sectionsToUpdate.setCosmetics(!this.importCosmetics());
    this.bumpSectionsRevision();
  }

  toggleImportRules() {
    const enabled = !this.importRules();
    this.sectionsToUpdate.setAllRules(enabled);
    this.syncGroupTogglesFromSections();
  }

  onRuleSectionToggle() {
    this.syncGroupTogglesFromSections();
  }

  toggleRuleFineTune() {
    this.showRuleFineTune.update(v => !v);
  }

  nameChangeKeyDown(event: KeyboardEvent) {
    const allowedChars = /^[a-zA-Z0-9 .-]$/;
    const char = event.key;

    if (event.ctrlKey || event.altKey || event.metaKey ||
      char.length > 1 || char === 'Backspace' || char === 'Delete' ||
      char === 'ArrowLeft' || char === 'ArrowRight' ||
      char === 'Tab' || char === 'Home' || char === 'End') {
      return;
    }

    if (!allowedChars.test(char)) {
      event.preventDefault();
    }
  }

  onNewFilterNameChange() {
    this.nameError = null;
  }

  toggleAllCustomTiers() {
    this.allCustomTiers = !this.allCustomTiers;
    this.sectionsToUpdate.customCurrencyAndDivCardTiers = this.allCustomTiers;
    this.sectionsToUpdate.customUniqueTiers = this.allCustomTiers;
    this.sectionsToUpdate.customChanceBaseTiers = this.allCustomTiers;
    this.sectionsToUpdate.customGearTiers = this.allCustomTiers;
    this.sectionsToUpdate.customJewelleryTiers = this.allCustomTiers;
    this.sectionsToUpdate.customEquipmentTiers = this.allCustomTiers;
    this.sectionsToUpdate.customModTiers = this.allCustomTiers;
    this.sectionsToUpdate.tieringModeCustom = this.allCustomTiers;
    this.sectionsToUpdate.divCardTieringModeCustom = this.allCustomTiers;
    this.sectionsToUpdate.uniquesTieringModeCustom = this.allCustomTiers;
    this.onRuleSectionToggle();
  }

  toggleAllMyEquipmentAndFlasks() {
    this.myEquipmentAndFlasks = !this.myEquipmentAndFlasks;
    this.sectionsToUpdate.myWeapons = this.myEquipmentAndFlasks;
    this.sectionsToUpdate.myArmour = this.myEquipmentAndFlasks;
    this.sectionsToUpdate.myJewellery = this.myEquipmentAndFlasks;
    this.sectionsToUpdate.myJewel = this.myEquipmentAndFlasks;
    this.sectionsToUpdate.myFlasks = this.myEquipmentAndFlasks;
    this.onRuleSectionToggle();
  }

  private syncGroupTogglesFromSections() {
    this.myEquipmentAndFlasks = this.sectionsToUpdate.myWeapons
      && this.sectionsToUpdate.myArmour
      && this.sectionsToUpdate.myJewellery
      && this.sectionsToUpdate.myJewel
      && this.sectionsToUpdate.myFlasks;
    this.allCustomTiers = this.sectionsToUpdate.customCurrencyAndDivCardTiers
      && this.sectionsToUpdate.customUniqueTiers
      && this.sectionsToUpdate.customChanceBaseTiers
      && this.sectionsToUpdate.customGearTiers
      && this.sectionsToUpdate.customJewelleryTiers
      && this.sectionsToUpdate.customEquipmentTiers
      && this.sectionsToUpdate.customModTiers
      && this.sectionsToUpdate.tieringModeCustom
      && this.sectionsToUpdate.divCardTieringModeCustom
      && this.sectionsToUpdate.uniquesTieringModeCustom;
    this.bumpSectionsRevision();
  }

  private bumpSectionsRevision() {
    this.sectionsRevision.update(n => n + 1);
  }

  private getDefaultNewFilterName(): string {
    return this.getUniqueFilterName(this.filterResults().importedName || 'Imported');
  }

  // Filter names are capped at MAX_FILTER_NAME_LENGTH (GGG limit). When the base name is
  // already taken, leave room for " N" so we never truncate back to the same taken name.
  private getUniqueFilterName(baseName: string): string {
    const maxLen = MAX_FILTER_NAME_LENGTH;
    const truncated = baseName.substring(0, maxLen);
    if (!this.isFilterNameTaken(truncated)) {
      return truncated;
    }

    let counter = 1;
    while (true) {
      const suffix = ` ${counter}`;
      const candidate = `${baseName.substring(0, maxLen - suffix.length)}${suffix}`;
      if (!this.isFilterNameTaken(candidate)) {
        return candidate;
      }
      counter++;
    }
  }

  private isFilterNameTaken(name: string): boolean {
    const storedNames = this.filterService.storedFilters.map(f => f.name);
    const accountFiltersJson = localStorage.getItem(LOCAL_STORAGE_KEY_ACCOUNT_FILTERS);
    const accountNames = accountFiltersJson
      ? (JSON.parse(accountFiltersJson) as PoeFilter[]).map(f => f.filter_name)
      : [];
    return storedNames.includes(name) || accountNames.includes(name);
  }
}
